import { useNavigate, useParams, useSearch, useRouterState } from '@tanstack/react-router'

/**
 * Single navigation hook for all of AI PRIME.
 *
 * Replaces:
 *   useAppViewStore(s => s.view)          → read from params/search
 *   pushView({ mode, tab, product })      → goMapTab() / goHome() / etc.
 *   resetToIntent()                       → goHome()
 *   goBack()                              → back()
 *
 * The hook is safe to call at any route depth — useParams/useSearch
 * are both called with strict:false so they return undefined rather
 * than throwing when a param doesn't exist at the current route.
 */
export function useAppNav() {
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const search = useSearch({ strict: false })
  const routerState = useRouterState()

  const pathname = routerState.location.pathname

  // ── Derived location flags ─────────────────────────────────────────────
  // These mirror the old isIntent / isDashboard / isMap booleans from App.jsx
  const isIntent = pathname === '/'
  const isDashboard = pathname.startsWith('/dashboard')
  const isMap = pathname.startsWith('/map')
  const isPlatformAdminRoute = pathname.startsWith('/platform-admin')
  const isField = pathname.startsWith('/field')
  // Subtree, not exact match: /portal/$planReviewId is a child route, so an
  // exact test would drop the strip's highlight the moment a reviewer opens a
  // plan. Same shape as the HOMES row in router.jsx, for the same reason.
  const isPortal = pathname === '/portal' || pathname.startsWith('/portal/')
  const isRoute = pathname.startsWith('/field/route')
  const isSite = pathname.startsWith('/field/site')

  // $tab from /map/$tab — the active product/section slug
  const activeTab = params?.tab ?? null

  // $dashId from /dashboard/$dashId
  const activeDash = params?.dashId ?? null

  // Search params
  const zoom = search?.zoom
  const lat = search?.lat
  const lng = search?.lng
  const mapMode = search?.mapMode ?? 'layers'

  // ── Navigation actions ─────────────────────────────────────────────────

  // Replaces: resetToIntent()
  function goHome() {
    navigate({ to: '/' })
  }

  // Replaces: pushView({ mode:'map', tab:'projects' })
  function goProjects() {
    navigate({ to: '/map/$tab', params: { tab: 'projects' } })
  }

  // /route — the field routing view. Here rather than a raw navigate() at the
  // call site because this hook's header states it is the single navigation
  // entry point for AI PRIME, and one exception is how that stops being true.
  function goField() {
    navigate({ to: '/field' })
  }

  // /portal — the Operator Portal. Here rather than a raw navigate() at the
  // call site for the reason stated above goField: this hook is the single
  // navigation entry point, and one exception is how that stops being true.
  function goPortal() {
    navigate({ to: '/portal' })
  }

  // /field/route, not /route — the page is a child of the FieldAssist shell.
  function goRoute() {
    navigate({ to: '/field/route' })
  }

  // /field/site — SiteAgent. Here for the same reason goRoute is: this hook's
  // header states it is the single navigation entry point for AI PRIME, and one
  // exception is how that stops being true.
  function goSite() {
    navigate({ to: '/field/site' })
  }

  // Replaces: pushView({ mode:'dashboard', detail:'landing' })
  function openDashboardLanding() {
    navigate({ to: '/dashboard' })
  }

  // Replaces: pushView({ mode:'dashboard', detail: id })
  function setActiveDash(id) {
    if (!id || id === 'landing') {
      navigate({ to: '/dashboard' })
    } else {
      navigate({ to: '/dashboard/$dashId', params: { dashId: id } })
    }
  }

  // Replaces: pushView({ mode:'map', tab:'map' })  (generic map open)
  function openMap(searchParams = {}) {
    navigate({
      to: '/map/$tab',
      params: { tab: 'explore' },
      search: searchParams,
    })
  }

  // Replaces: pushView({ mode:'map', tab: tabName, product: tabName })
  // Used by TopNav mode strip and IntentSelectorPage product cards
  function goMapTab(tabSlug) {
    navigate({ to: '/map/$tab', params: { tab: tabSlug } })
  }

  // Day 5-pulled-into-Day-1: platform admins can now navigate to and from
  // the platform admin UI freely. AppShell intercepts the URL to render
  // PlatformAdminLayout full-viewport (no AppShell chrome).
  function goPlatformAdmin() {
    navigate({ to: '/platform-admin' })
  }

  // Viewport sync — called on MapLibre 'moveend', replace:true keeps
  // history clean (pan/zoom shouldn't create 50 back-button entries)
  function syncViewport({ zoom, lat, lng }) {
    navigate({
      search: (prev) => ({
        ...prev,
        zoom: +zoom.toFixed(4),
        lat: +lat.toFixed(6),
        lng: +lng.toFixed(6),
      }),
      replace: true,
    })
  }

  // mapMode (layers | aoi) in search params — replace:true, same reason
  function setMapMode(mode) {
    navigate({
      search: (prev) => ({ ...prev, mapMode: mode }),
      replace: true,
    })
  }

  // Replaces: goBack()
  function back() {
    window.history.back()
  }

  // Inline notification nav — replaces dispatchNotificationAction's pushView calls.
  // Non-nav side effects (aoi store) are handled in the onAction callback in App.jsx.
  function dispatchNotificationNav(notif) {
    if (!notif?.action) return
    switch (notif.action.type) {
      case 'map-focus':
        navigate({ to: '/map/$tab', params: { tab: 'explore' } })
        break

      case 'job-result':
        navigate({ to: '/map/$tab', params: { tab: 'helios' } })
        break

      default:
        console.warn('Unhandled notification action type:', notif.action.type)
    }
  }

  return {
    // ── Location state ───────────────────────────────────────────────────
    isIntent,
    isDashboard,
    isMap,
    isPlatformAdminRoute,
    isField,
    isPortal,
    isRoute,
    isSite,
    activeTab,
    activeDash,
    zoom,
    lat,
    lng,
    mapMode,

    // ── Actions ──────────────────────────────────────────────────────────
    goHome,
    goProjects,
    goField,
    goPortal,
    goRoute,
    goSite,
    openDashboardLanding,
    setActiveDash,
    openMap,
    goMapTab,
    goPlatformAdmin,
    syncViewport,
    setMapMode,
    back,
    dispatchNotificationNav,
  }
}